/**
 * AdminEditorialCalendar — CMS-driven task board that tells admins
 * (and, later, university owners) what needs attention this week vs
 * next 30 days vs later. Three colour-coded categories:
 *   watch (green)         · data feed refreshes landing on a cadence
 *   verification (amber)  · admin review windows post-refresh
 *   editorial (rose)      · calendar moments (Clearing, UCAS, Freshers)
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CalendarClock, Check, ExternalLink, FileDown, History, Mail, Plus, ShieldAlert, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";


const CAT = {
  watch:          { label: "Watch",         chip: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  verification:   { label: "Verification",  chip: "bg-amber-50 text-amber-800 border-amber-200" },
  editorial:      { label: "Editorial",     chip: "bg-rose-50 text-rose-800 border-rose-200" },
  manual_ingest:  { label: "Manual CSV",    chip: "bg-indigo-50 text-indigo-800 border-indigo-200" },
};


/**
 * Render a comment body with @mention tokens highlighted. Returns a
 * mixed array of strings + <mark> spans so React handles keying and
 * whitespace naturally.
 */
function renderBodyWithMentions(body) {
  if (!body) return null;
  const parts = [];
  const re = /(^|\s)(@[A-Za-z0-9._+-]+(?:@[A-Za-z0-9.-]+)?)/g;
  let last = 0;
  let m;
  let idx = 0;
  while ((m = re.exec(body)) !== null) {
    if (m.index > last) parts.push(body.slice(last, m.index));
    parts.push(m[1]);
    parts.push(
      <mark
        key={`mention-${idx++}`}
        className="bg-indigo-100 text-indigo-800 rounded px-1 font-mono text-[13px]"
      >
        {m[2]}
      </mark>
    );
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push(body.slice(last));
  return parts;
}


function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const now = new Date();
  const diffDays = Math.round((d - now) / (1000 * 60 * 60 * 24));
  const abs = d.toLocaleDateString("en-GB", { weekday: "short", month: "short", day: "numeric" });
  if (diffDays < 0) return `${abs} · ${Math.abs(diffDays)}d overdue`;
  if (diffDays === 0) return `${abs} · today`;
  if (diffDays === 1) return `${abs} · tomorrow`;
  return `${abs} · in ${diffDays}d`;
}


export default function AdminEditorialCalendar() {
  const { user, login } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);
  const [creating, setCreating] = useState(false);
  // Ingest-history drawer state — one open task at a time.
  const [historyTask, setHistoryTask] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/editorial-calendar");
      setData(r.data);
      setForbidden(false);
    } catch (e) {
      if ([401, 403].includes(e?.response?.status)) setForbidden(true);
      else toast.error(e?.response?.data?.detail || "Load failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const complete = async (t, ingestNote) => {
    try {
      const body = { completed: true };
      if (ingestNote) body.ingest_note = ingestNote;
      await api.patch(`/admin/editorial-calendar/${t.id}`, body);
      toast.success(t.cadence ? `Advanced by ${t.cadence}` : "Task archived");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const seed = async () => {
    if (!window.confirm("Populate the baseline recurring tasks + manual-ingest sources?")) return;
    try {
      const r = await api.post("/admin/editorial-calendar/seed");
      toast.success(`Seeded ${r.data.inserted} new tasks (${r.data.skipped} already present)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Seed failed");
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try {
      await api.delete(`/admin/editorial-calendar/${t.id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const sendDigest = async () => {
    if (!window.confirm("Send the Monday-morning digest email to admin editors now?")) return;
    setSendingDigest(true);
    try {
      const r = await api.post("/admin/editorial-calendar/send-digest");
      toast.success(`Digest emailed to ${r.data.recipients.length} editor(s) · ${r.data.total_deadlines} deadlines listed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Digest failed");
    } finally {
      setSendingDigest(false);
    }
  };

  const backfillRunners = async () => {
    try {
      const r = await api.post("/admin/editorial-calendar/backfill-runner-scripts");
      const unmapped = r.data.unmapped?.length || 0;
      toast.success(
        `Wired ${r.data.updated} task${r.data.updated === 1 ? "" : "s"}${unmapped ? ` · ${unmapped} still without a matching seed script` : ""}`
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    }
  };

  if (forbidden) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-16 text-center" data-testid="cal-forbidden">
        <ShieldAlert className="w-8 h-8 text-stone-400 mx-auto mb-3" />
        <h1 className="font-editorial text-2xl">Admin access required</h1>
        <p className="text-sm text-stone-600 mt-1">The editorial calendar is restricted to admin editors.</p>
        {!user && (
          <Button onClick={login} className="mt-4" data-testid="cal-signin-btn">Sign in with Google</Button>
        )}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 py-10" data-testid="admin-editorial-calendar-page">
      <header className="mb-8">
        <div className="flex items-center gap-3">
          <CalendarClock className="w-5 h-5 text-stone-500" />
          <h1 className="font-editorial text-4xl">Editorial calendar</h1>
        </div>
        <p className="mt-2 text-stone-600 text-sm max-w-2xl">
          What to do this week, next 30 days, and later — grouped by whether it's a data
          feed to watch, a verification window to drain, or an editorial moment to publish for.
        </p>
        <div className="mt-4 flex flex-wrap gap-2 items-center">
          {Object.entries(CAT).map(([key, v]) => (
            <span key={key} className={`text-[11px] px-2 py-1 border rounded-full ${v.chip}`}>
              {v.label} · {data?.counts?.[key] ?? 0}
            </span>
          ))}
          <span className="text-[11px] px-2 py-1 border rounded-full bg-white border-stone-200 text-stone-600">
            {data?.total ?? 0} total
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={sendDigest}
            disabled={sendingDigest}
            className="ml-auto text-xs"
            data-testid="cal-send-digest-btn"
          >
            <Mail className="w-3.5 h-3.5 mr-1.5" />
            {sendingDigest ? "Sending…" : "Send weekly digest"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={backfillRunners}
            className="text-xs"
            data-testid="cal-backfill-runners-btn"
            title="Re-attach the matching seed script to every manual-CSV task"
          >
            Rewire runner scripts
          </Button>
        </div>
      </header>

      <NewTaskForm open={creating} setOpen={setCreating} onSaved={load} onSeed={seed} />

      {loading && !data && (
        <div className="text-stone-500 text-sm">Loading…</div>
      )}
      {data && (
        <>
          <Section title="This week" tone="urgent" rows={data.this_week} onComplete={complete} onRemove={remove} onHistory={setHistoryTask} testIdPrefix="week" />
          <Section title="Next 30 days" tone="normal" rows={data.next_30_days} onComplete={complete} onRemove={remove} onHistory={setHistoryTask} testIdPrefix="month" />
          <Section title="Later" tone="muted" rows={data.later} onComplete={complete} onRemove={remove} onHistory={setHistoryTask} testIdPrefix="later" />
        </>
      )}

      <IngestHistoryDrawer task={historyTask} onClose={() => setHistoryTask(null)} onRefresh={load} />
    </div>
  );
}


function Section({ title, tone, rows, onComplete, onRemove, onHistory, testIdPrefix }) {
  if (!rows || rows.length === 0) {
    return (
      <section className="mt-8" data-testid={`cal-section-${testIdPrefix}`}>
        <h2 className={`font-editorial text-lg mb-2 ${tone === "muted" ? "text-stone-500" : ""}`}>{title}</h2>
        <p className="text-xs text-stone-400 italic">Nothing scheduled.</p>
      </section>
    );
  }
  return (
    <section className="mt-8" data-testid={`cal-section-${testIdPrefix}`}>
      <h2 className={`font-editorial text-lg mb-2 ${tone === "muted" ? "text-stone-500" : ""}`}>
        {title} <span className="text-xs font-mono text-stone-500">({rows.length})</span>
      </h2>
      <ul className="border border-stone-200 rounded-lg bg-white divide-y divide-stone-100 overflow-hidden">
        {rows.map((t) => (
          <TaskRow key={t.id} t={t} onComplete={onComplete} onRemove={onRemove} onHistory={onHistory} testIdPrefix={testIdPrefix} />
        ))}
      </ul>
    </section>
  );
}


function IngestHistoryDrawer({ task, onClose, onRefresh }) {
  const [tab, setTab] = useState("history");
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [runScript, setRunScript] = useState(false);
  const [roster, setRoster] = useState([]);
  const [mentionMenu, setMentionMenu] = useState(null);   // { query, position }
  const commentBoxRef = useRef(null);

  useEffect(() => {
    setTab("history");
    setUploadResult(null);
    setNewComment("");
    if (!task) return;
    api.get(`/admin/editorial-calendar/${task.id}/comments`)
       .then((r) => setComments(r.data.comments || []))
       .catch(() => setComments([]));
    // Load the mention roster once — small list, safe to eager-fetch.
    api.get("/admin/editorial-calendar/mention-roster")
       .then((r) => setRoster(r.data.roster || []))
       .catch(() => setRoster([]));
  }, [task?.id]);

  const history = (task?.ingest_history || []).slice().reverse();
  const canRunScript = !!task?.runner_script;

  // Filter the mention roster to whatever comes after the most recent
  // unclosed `@` in the textbox.
  const filteredRoster = useMemo(() => {
    if (!mentionMenu) return [];
    const q = (mentionMenu.query || "").toLowerCase();
    const scored = roster
      .filter((r) => r.handle.toLowerCase().startsWith(q) || r.email.toLowerCase().startsWith(q))
      .slice(0, 6);
    return scored;
  }, [mentionMenu, roster]);

  const handleCommentChange = (value, cursorPos) => {
    setNewComment(value);
    // Look back from cursor for an "@" that isn't preceded by non-space
    // (so we don't trigger on emails inside URLs).
    const upToCursor = value.slice(0, cursorPos);
    const match = upToCursor.match(/(^|\s)@([A-Za-z0-9._+-]*)$/);
    if (match) {
      setMentionMenu({ query: match[2], cursor: 0, atIndex: cursorPos - match[2].length - 1 });
    } else {
      setMentionMenu(null);
    }
  };

  const handleCommentKeyDown = (e) => {
    if (!mentionMenu || filteredRoster.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMentionMenu({ ...mentionMenu, cursor: (mentionMenu.cursor + 1) % filteredRoster.length });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setMentionMenu({
        ...mentionMenu,
        cursor: (mentionMenu.cursor - 1 + filteredRoster.length) % filteredRoster.length,
      });
    } else if (e.key === "Enter" || e.key === "Tab") {
      e.preventDefault();
      acceptMention(filteredRoster[mentionMenu.cursor]);
    } else if (e.key === "Escape") {
      setMentionMenu(null);
    }
  };

  const acceptMention = (mention) => {
    if (!mentionMenu) return;
    // Replace the partial "@..." token in the box with "@handle ".
    const before = newComment.slice(0, mentionMenu.atIndex);
    const afterStart = mentionMenu.atIndex + 1 + (mentionMenu.query || "").length;
    const after = newComment.slice(afterStart);
    const updated = `${before}@${mention.handle} ${after}`;
    setNewComment(updated);
    setMentionMenu(null);
    // Return focus to the textarea after the replacement.
    setTimeout(() => commentBoxRef.current?.focus(), 0);
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      const r = await api.post(`/admin/editorial-calendar/${task.id}/comments`, {
        body: newComment.trim(),
      });
      setComments((cs) => [...cs, r.data]);
      setNewComment("");
      setMentionMenu(null);
      if (r.data.mentioned?.length) {
        if (r.data.mention_email_error) {
          toast.warning(
            `Comment posted · mention email failed: ${r.data.mention_email_error.slice(0, 90)}…`
          );
        } else {
          toast.success(
            `Comment posted · ${r.data.mentioned.length} colleague${r.data.mentioned.length === 1 ? "" : "s"} emailed`
          );
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Comment failed");
    }
  };

  const deleteComment = async (c) => {
    if (!window.confirm("Delete this comment?")) return;
    try {
      await api.delete(`/admin/editorial-calendar/${task.id}/comments/${c.id}`);
      setComments((cs) => cs.filter((x) => x.id !== c.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const uploadFile = async (fileList) => {
    if (!fileList?.length) return;
    const file = fileList[0];
    if (file.size > 25 * 1024 * 1024) {
      toast.error("File too large (max 25 MB)");
      return;
    }
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(
        `/admin/editorial-calendar/${task.id}/upload?run_script=${runScript}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setUploadResult(r.data);
      toast.success(
        r.data.script_run
          ? "Uploaded + seed script executed"
          : "Uploaded to object storage"
      );
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const isManual = task?.category === "manual_ingest";

  return (
    <Dialog open={!!task} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="ingest-history-drawer">
        <DialogHeader>
          <DialogTitle className="font-editorial">{task?.title}</DialogTitle>
          <DialogDescription className="text-xs">
            {history.length} log entr{history.length === 1 ? "y" : "ies"}
            {task?.cadence ? ` · recurs ${task.cadence}` : ""}
            {" · "}
            {comments.length} comment{comments.length === 1 ? "" : "s"}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b border-stone-200 -mt-2" data-testid="drawer-tabs">
          {[
            ["history", `History (${history.length})`],
            ["comments", `Comments (${comments.length})`],
            ...(isManual ? [["upload", "Upload file"]] : []),
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              data-testid={`drawer-tab-${key}`}
              className={
                "px-3 py-2 text-[11px] font-underground-sc tracking-wider border-b-2 transition -mb-px " +
                (tab === key
                  ? "border-[#002147] text-[#002147]"
                  : "border-transparent text-stone-500 hover:text-stone-800")
              }
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "history" && (
          history.length === 0 ? (
            <p className="text-sm text-stone-500 italic py-8 text-center">
              No ingests logged yet. Complete this task once and the log will start filling.
            </p>
          ) : (
            <ol className="max-h-[420px] overflow-y-auto divide-y divide-stone-100 border border-stone-200 rounded-md"
                data-testid="ingest-history-list">
              {history.map((h, i) => (
                <li key={i} className="p-3" data-testid={`ingest-history-row-${i}`}>
                  <div className="flex items-baseline gap-2 mb-0.5">
                    <span className="text-xs font-mono text-stone-900">
                      {new Date(h.at).toLocaleString("en-GB", {
                        day: "numeric", month: "short", year: "numeric",
                        hour: "2-digit", minute: "2-digit",
                      })}
                    </span>
                    {h.by && (
                      <span className="text-[11px] font-mono text-stone-500">· {h.by}</span>
                    )}
                    {h.script_run && (
                      <span className={"text-[10px] font-underground-sc tracking-wider px-1.5 rounded " + (h.script_error ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700")}>
                        {h.script_error ? "script failed" : "script ran"}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-stone-700 whitespace-pre-wrap">
                    {h.ingest_note || <span className="italic text-stone-400">no note recorded</span>}
                  </p>
                  {h.script_output && (
                    <pre className="mt-1 text-[10px] font-mono bg-stone-50 border border-stone-200 rounded p-2 max-h-32 overflow-auto text-stone-700">{h.script_output}</pre>
                  )}
                  {h.script_error && (
                    <pre className="mt-1 text-[10px] font-mono bg-red-50 border border-red-200 rounded p-2 max-h-32 overflow-auto text-red-700">{h.script_error}</pre>
                  )}
                </li>
              ))}
            </ol>
          )
        )}

        {tab === "comments" && (
          <div className="space-y-3" data-testid="drawer-comments">
            {comments.length === 0 ? (
              <p className="text-sm text-stone-500 italic py-6 text-center">
                No comments yet — first to flag something?
              </p>
            ) : (
              <ol className="max-h-[300px] overflow-y-auto divide-y divide-stone-100 border border-stone-200 rounded-md">
                {comments.map((c) => (
                  <li key={c.id} className="p-3" data-testid={`comment-row-${c.id}`}>
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-[11px] font-mono text-stone-900">{c.author}</span>
                      <span className="text-[10px] font-mono text-stone-500">
                        · {new Date(c.at).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </span>
                      {c.mentioned?.length > 0 && (
                        <span className="text-[10px] font-mono text-indigo-700"
                              data-testid={`comment-mentions-${c.id}`}>
                          · notified {c.mentioned.map((m) => "@" + m.split("@")[0]).join(", ")}
                        </span>
                      )}
                      {c.mention_email_error && (
                        <span className="text-[10px] font-mono text-red-700"
                              title={c.mention_email_error}>
                          · email failed
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => deleteComment(c)}
                        className="ml-auto text-stone-400 hover:text-red-600 text-[11px]"
                        data-testid={`comment-delete-${c.id}`}
                        title="Delete (yours only)"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                    <p className="text-sm text-stone-800 mt-0.5 whitespace-pre-wrap">
                      {renderBodyWithMentions(c.body)}
                    </p>
                  </li>
                ))}
              </ol>
            )}
            <div className="relative">
              <div className="flex gap-2">
                <Textarea
                  ref={commentBoxRef}
                  rows={2}
                  value={newComment}
                  onChange={(e) => handleCommentChange(e.target.value, e.target.selectionStart)}
                  onKeyDown={handleCommentKeyDown}
                  placeholder="Flag an ambiguity, note a header change, @-tag a colleague…"
                  data-testid="comment-new-body"
                  className="text-sm"
                />
                <Button
                  size="sm"
                  onClick={addComment}
                  disabled={!newComment.trim()}
                  data-testid="comment-new-submit"
                >
                  Post
                </Button>
              </div>
              {mentionMenu && filteredRoster.length > 0 && (
                <div
                  className="absolute left-0 bottom-full mb-1 w-64 border border-stone-200 bg-white rounded-md shadow-lg z-50 max-h-40 overflow-y-auto"
                  data-testid="mention-menu"
                >
                  {filteredRoster.map((m, i) => (
                    <button
                      key={m.email}
                      type="button"
                      onClick={() => acceptMention(m)}
                      data-testid={`mention-option-${m.handle}`}
                      className={
                        "block w-full text-left px-3 py-1.5 text-sm hover:bg-indigo-50 " +
                        (i === mentionMenu.cursor ? "bg-indigo-50" : "")
                      }
                    >
                      <span className="font-mono text-indigo-700">@{m.handle}</span>
                      <span className="text-[11px] text-stone-500 ml-2 font-mono">{m.email}</span>
                    </button>
                  ))}
                </div>
              )}
              <p className="text-[10px] text-stone-500 mt-1 font-mono">
                Tip: type <code>@</code> and pick a colleague — they'll get an email straight away.
              </p>
            </div>
          </div>
        )}

        {tab === "upload" && isManual && (
          <div className="space-y-3" data-testid="drawer-upload">
            <label className="block border-2 border-dashed border-stone-300 rounded-lg p-8 text-center cursor-pointer hover:border-indigo-500 hover:bg-indigo-50/30 transition">
              <input
                type="file"
                onChange={(e) => uploadFile(e.target.files)}
                className="hidden"
                data-testid="upload-file-input"
                accept=".csv,.xlsx,.xls,.pdf,.json,.txt"
              />
              <FileDown className="w-6 h-6 text-stone-400 mx-auto mb-2" />
              <div className="text-sm text-stone-700 font-medium">
                {uploading ? "Uploading…" : "Choose a CSV / XLSX / PDF"}
              </div>
              <div className="text-[11px] text-stone-500 mt-1 font-mono">
                {task.file_hint ? `Expected: ${task.file_hint}` : "Max 25 MB"}
              </div>
            </label>
            <label className="flex items-center gap-2 text-xs text-stone-700"
                   title={canRunScript ? "" : "This task has no seed-script wired"}>
              <input
                type="checkbox"
                checked={runScript}
                onChange={(e) => setRunScript(e.target.checked)}
                disabled={!canRunScript}
                data-testid="upload-run-script"
              />
              <span>
                Auto-run seed script after upload
                {task.runner_script && (
                  <code className="ml-1 text-[10px] bg-stone-100 px-1 rounded font-mono">
                    {task.runner_script}
                  </code>
                )}
                {!canRunScript && (
                  <span className="ml-1 text-[11px] text-stone-500">
                    (no runner_script configured on this task)
                  </span>
                )}
              </span>
            </label>
            {uploadResult && (
              <div className="text-[11px] font-mono border border-stone-200 rounded p-3 bg-stone-50" data-testid="upload-result">
                <div>path · {uploadResult.storage_path}</div>
                <div>size · {uploadResult.size_bytes?.toLocaleString?.() || uploadResult.size_bytes} bytes</div>
                {uploadResult.script_output && (
                  <pre className="mt-2 whitespace-pre-wrap text-emerald-800">{uploadResult.script_output}</pre>
                )}
                {uploadResult.script_error && (
                  <pre className="mt-2 whitespace-pre-wrap text-red-700">{uploadResult.script_error}</pre>
                )}
              </div>
            )}
          </div>
        )}

        {task?.source_url && (
          <a
            href={task.source_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-[11px] text-indigo-700 hover:text-indigo-900 font-mono mt-2"
          >
            <FileDown className="w-3 h-3" /> Fetch fresh from {task.source_url.replace(/^https?:\/\//, "").slice(0, 60)}
          </a>
        )}
      </DialogContent>
    </Dialog>
  );
}


function TaskRow({ t, onComplete, onRemove, onHistory, testIdPrefix }) {
  const chip = CAT[t.category]?.chip || "bg-stone-100";
  const label = CAT[t.category]?.label || t.category;
  const isOverdue = (new Date(t.next_due) - new Date()) < 0;
  const isManual = t.category === "manual_ingest";
  const historyCount = (t.ingest_history || []).length;

  const handleComplete = () => {
    if (isManual) {
      const note = window.prompt(
        `Log ingest for "${t.title}" — where did you save the file? ` +
        `(filename, drive URL, script command…) Leave blank to skip logging.`
      );
      // window.prompt returns null on Cancel — treat as abort.
      if (note === null) return;
      onComplete(t, note.trim() || undefined);
    } else {
      onComplete(t);
    }
  };

  return (
    <li className="p-3 flex items-start gap-3" data-testid={`${testIdPrefix}-row-${t.id}`}>
      <span className={`text-[10px] font-underground-sc tracking-wider px-1.5 py-0.5 rounded border ${chip} shrink-0 mt-0.5`}>
        {label}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-stone-900 truncate">
          {t.title}
        </div>
        <div className="text-[11px] text-stone-500 mt-0.5 flex flex-wrap items-center gap-2">
          <span className={isOverdue ? "text-rose-700 font-mono" : "font-mono"}>
            {fmtWhen(t.next_due)}
          </span>
          {t.cadence && <span className="font-mono">recurs {t.cadence}</span>}
          {t.owner && <span>· owner {t.owner}</span>}
          {t.notes && <span className="truncate max-w-md">· {t.notes}</span>}
        </div>
        {isManual && (t.source_url || t.file_hint) && (
          <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[11px]"
               data-testid={`${testIdPrefix}-manual-hint-${t.id}`}>
            {t.source_url && (
              <a
                href={t.source_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-indigo-700 hover:text-indigo-900 font-mono"
                data-testid={`${testIdPrefix}-source-${t.id}`}
              >
                <FileDown className="w-3 h-3" /> {t.source_url.replace(/^https?:\/\//, "").slice(0, 55)}
              </a>
            )}
            {t.file_hint && (
              <span className="font-mono text-stone-500">
                expected: <span className="text-stone-700">{t.file_hint}</span>
              </span>
            )}
          </div>
        )}
        {t.last_completed_at && (
          <div className="text-[10px] font-mono text-stone-400 mt-1"
               data-testid={`${testIdPrefix}-lastdone-${t.id}`}>
            last ingested {new Date(t.last_completed_at).toLocaleDateString("en-GB", { year: "numeric", month: "short", day: "numeric" })}
            {t.last_completed_by ? ` · by ${t.last_completed_by}` : ""}
            {t.ingest_history?.length && t.ingest_history[t.ingest_history.length - 1]?.ingest_note
              ? ` · "${t.ingest_history[t.ingest_history.length - 1].ingest_note}"`
              : ""}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1 shrink-0">
        {t.linked_url && (
          <Link
            to={t.linked_url}
            className="text-[11px] text-stone-500 hover:text-stone-900 flex items-center gap-1"
            data-testid={`${testIdPrefix}-open-${t.id}`}
          >
            Open <ExternalLink className="w-3 h-3" />
          </Link>
        )}
        {(historyCount > 0 || isManual) && onHistory && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onHistory(t)}
            className="h-7 text-[11px] px-2 text-stone-600 hover:text-indigo-700"
            data-testid={`${testIdPrefix}-history-${t.id}`}
            title={`View ${historyCount} past ingest${historyCount === 1 ? "" : "s"}`}
          >
            <History className="w-3 h-3 mr-1" /> {historyCount || 0}
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={handleComplete}
          className="h-7 text-xs"
          data-testid={`${testIdPrefix}-complete-${t.id}`}
        >
          <Check className="w-3 h-3 mr-1" /> {isManual ? "Log ingest" : "Done"}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onRemove(t)}
          className="h-7 w-7 p-0 text-stone-400 hover:text-rose-600"
          data-testid={`${testIdPrefix}-delete-${t.id}`}
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>
    </li>
  );
}


function NewTaskForm({ open, setOpen, onSaved, onSeed }) {
  const [form, setForm] = useState({
    title: "", category: "verification", next_due: "",
    cadence: "", owner: "", linked_url: "", notes: "",
    source_url: "", file_hint: "",
  });
  const submit = async () => {
    if (!form.title || !form.next_due) {
      toast.error("Title and next-due are required");
      return;
    }
    try {
      await api.post("/admin/editorial-calendar", {
        ...form,
        next_due: form.next_due.length === 10 ? `${form.next_due}T00:00:00+00:00` : form.next_due,
        cadence: form.cadence || null,
        owner: form.owner || null,
        linked_url: form.linked_url || null,
        notes: form.notes || null,
        source_url: form.source_url || null,
        file_hint: form.file_hint || null,
      });
      toast.success("Task added");
      setForm({ title: "", category: "verification", next_due: "", cadence: "", owner: "", linked_url: "", notes: "", source_url: "", file_hint: "" });
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add failed");
    }
  };

  const isManual = form.category === "manual_ingest";

  return (
    <div className="mb-6 flex flex-wrap gap-2 items-start" data-testid="cal-new-task-panel">
      {!open ? (
        <>
          <Button variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="cal-new-task-open">
            <Plus className="w-3 h-3 mr-1" /> Add task
          </Button>
          {onSeed && (
            <Button variant="ghost" size="sm" onClick={onSeed} data-testid="cal-seed-btn">
              Seed baseline recurring tasks
            </Button>
          )}
        </>
      ) : (
        <div className="w-full p-4 border border-stone-300 rounded-lg bg-white space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="cal-new-title" />
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <select
                className="mt-1 w-full h-9 px-2 border border-stone-300 rounded-md text-sm bg-white"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="cal-new-category"
              >
                <option value="watch">Watch (data refresh)</option>
                <option value="verification">Verification (admin review)</option>
                <option value="editorial">Editorial (calendar moment)</option>
                <option value="manual_ingest">Manual CSV (editor fetch)</option>
              </select>
            </div>
            <div>
              <Label className="text-xs">Next due (YYYY-MM-DD)</Label>
              <Input type="date" value={form.next_due} onChange={(e) => setForm({ ...form, next_due: e.target.value })} data-testid="cal-new-due" />
            </div>
            <div>
              <Label className="text-xs">Cadence (P1W · P1M · P3M · P1Y)</Label>
              <Input value={form.cadence} onChange={(e) => setForm({ ...form, cadence: e.target.value })} placeholder="Leave blank for one-off" data-testid="cal-new-cadence" />
            </div>
            <div>
              <Label className="text-xs">Owner (optional)</Label>
              <Input value={form.owner} onChange={(e) => setForm({ ...form, owner: e.target.value })} data-testid="cal-new-owner" />
            </div>
            <div className="md:col-span-3">
              <Label className="text-xs">Linked URL (optional)</Label>
              <Input value={form.linked_url} onChange={(e) => setForm({ ...form, linked_url: e.target.value })} placeholder="/admin/verify or /uni-portal/edit" data-testid="cal-new-url" />
            </div>
            {isManual && (
              <>
                <div className="md:col-span-2">
                  <Label className="text-xs">Source URL (where to fetch the CSV / PDF)</Label>
                  <Input value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} placeholder="https://www.qilt.edu.au/surveys/…" data-testid="cal-new-source-url" />
                </div>
                <div>
                  <Label className="text-xs">Expected filename</Label>
                  <Input value={form.file_hint} onChange={(e) => setForm({ ...form, file_hint: e.target.value })} placeholder="ses-international-report.pdf" data-testid="cal-new-file-hint" />
                </div>
              </>
            )}
            <div className="md:col-span-3">
              <Label className="text-xs">Notes</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="cal-new-notes" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)} data-testid="cal-new-cancel">Cancel</Button>
            <Button size="sm" onClick={submit} data-testid="cal-new-submit">Add task</Button>
          </div>
        </div>
      )}
    </div>
  );
}
